import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import {md5OfFile, setHtmlRemovePrint, updateImagePreview} from "@/utils";
import {setFindItemId} from "@/utils/menu"
import axios from "axios";
import {ellipsis} from "@/utils";
import moment from "moment";
import {foldTelFields, stripHtml} from "@/utils/admin";

/**
 * @typedef {object} SearchMap
 * @property {string} searchType
 * @property {string} searchString
 * @property {number} offset
 */


/**
 * 관리자 메뉴에서 생성된 컨텐츠 데이터 상태 관리 Store
 */
export class BoardDetailStore {
    /**
     * 공통 검색 관련 Store
     * @type {SearchBaseStore}
     */
    searchBaseStore;

    /**
     * 게시판 리스트 데이터
     * @type {Array<Object>}
     */
    dataList = [];

    /**
     * 게시판 전체 데이터 개수
     * @type {number}
     */
    totalCount = 0;

    /**
     * 게시판 내부 상세 데이터
     * @type {Object}
     */
    listInnerData = {}

    /**
     * 게시판 검색 파라미터
     * @type {SearchMap}
     */
    searchMap = {
        searchType: "",
        searchString: "",
        offset: 0
    }

    /**
     * FORM 검색 SelectBox 리스트
     * @type {Array<Object>}
     */
    formSearchSelectList = [];

    /**
     * 업로드 파일 리스트
     * @type {Array<File|Object|string>}
     */
    formFile = [];

    /**
     * 현재 게시판 메뉴 데이터
     * @type {Object|null}
     */
    currentBoardContent = null;

    /**
     * 게시판 HTML 데이터
     * @type {string|null}
     */
    listHtml = null;

    /**
     * 게시글 작성자
     * @type {string|null}
     */
    boardWriter = null;

    /**
     * 게시글 생성일
     * @type {string|null}
     */
    boardCreatDate = null;

    /**
     * FORM 테이블 컬럼 데이터
     * @type {Array<Object>}
     */
    formTableInner = [];

    /**
     * 뉴스레터 구독 리스트
     * @type {Array<Object>}
     */
    newsSubscribeList = [];

    /**
     * 뉴스레터 구독 총 개수
     * @type {number}
     */
    newsSubscribeTotalCount = 0;

    /**
     * 뉴스레터 검색 상태값
     * @type {string}
     */
    searchSubscribeState = "";

    /**
     * 협력사 브랜드 정렬 리스트
     * @type {Array<Object>}
     */
    cooperationBradOrderList = [];

    /**
     * 이미지 미리보기 데이터 맵
     * @type {Object}
     */
    imagePreviewMap = {};


    constructor(store) {
        this.store = store;
        this.searchBaseStore = store.searchBaseStore;
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * 게시판 리스트 조회 함수
     *
     * @param {string} params URL Query String
     * @returns {Promise<void>}
     */
    async setFetchBoardList(params) {
        const search = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
        if (search.get("menu-id")) {
            search.set("menuId", search.get("menu-id"));
            search.delete("menu-id");
        }
        if (search.get("searchType")) {
            if(search.get("searchType") === "title"){
                search.set("searchType", "T");
            } else if(search.get("searchType") === "content"){
                search.set("searchType", "C");
            }
        }
        if (!search.get("searchString")) {
            search.delete("searchType");
        }
        const finalParams = `?${search.toString()}`;
        const response = await axios.get(`/api/content${finalParams}`);
        if(response){
            this.setListCount(response.data.data.totalCount);
            const resultData = [];
            for (const row of response.data.data.contents) {
                if(row.data.files){
                    for (const file of row.data.files) {
                        this.setFormFilesData(file);
                    }
                }
                row.data["_id"] = row._id;
                if(row.createdAt) row.data["createdAt"] = row.createdAt;
                if(row.updatedAt) row.data["updatedAt"] = row.updatedAt;
                if(row.writer) row.data["writer"] = row.writer
                resultData.push(row.data);
            }
            this.setDataList(resultData);
        }
    }

    /**
     * 게시판 전체 개수 세팅 함수
     *
     * @param {number} count 게시판 총 개수
     */
    setListCount (count = 0) {
        this.totalCount = count;
    }

    /**
     * 게시판 리스트 데이터 세팅 함수
     *
     * @param {Array<Object>} list 게시판 리스트
     */
    setDataList(list = []){
        this.dataList = list;
    }

    /**
     * 게시판 상세 데이터 조회 및 수정시 데이터 세팅 함수
     *
     * @param {string} id 컨텐츠 ID
     * @returns {Promise<void>}
     */
    async setFetchBoardData(id) {
        const response = await axios.get(`/api/content/detail?contentId=${id}`);
        if(response.data.data){
            const resData = response.data.data.content;
            if(resData.createdAt){
                this.boardCreatDate = resData.createdAt;
            }
            if(resData.writer){
                this.boardWriter = resData.writer;
            }
            if(resData.data.classification){
                this.setCooperationDataList("classification", resData.data.classification);
            }
            if(resData.data.title){
                this.setCooperationDataList("title", resData.data.title);
            }
            if(resData.data.listContent){
                this.setListSettingEditor(resData.data.listContent)
            }
            if(resData.data.files){
                this.setFormFilesData(resData.data.files);
            }
            if(resData.data.isActive){
                this.setCooperationDataList("isActive", resData.data.isActive);
            }
            if(resData.data.startDate){
                this.setCooperationDataList("startDate", resData.data.startDate);
            }
            if(resData.data.endDate){
                this.setCooperationDataList("endDate", resData.data.endDate);
            }
            if(resData.data.link){
                this.setCooperationDataList("link", resData.data.link);
            }
        }
    }

    /**
     * 업로드 파일 및 미리보기 데이터 세팅 함수
     *
     * @param {Array<File|Object|string>} files 파일 리스트
     */
    setFormFilesData(files = []) {
        this.formFile = files;
        this.imagePreviewMap = updateImagePreview(files);
    }

    /**
     * 업로드 파일 삭제 함수
     *
     * @param {number} index 삭제할 파일 인덱스
     */
    setRemoveFormFile(index) {
        const next = [...this.formFile];
        next.splice(index, 1);
        this.formFile = next;
        this.imagePreviewMap = updateImagePreview(next);
    }

    /**
     * 협력사 상세 데이터 세팅 함수
     *
     * @param {string} type 데이터 타입
     * @param {*} value 저장할 값
     */
    setCooperationDataList(type, value) {
        this.listInnerData[type] = value;
    }

    /**
     * 에디터 Base64 이미지 HTML 변환 함수
     *
     * @param {string} html HTML 문자열
     * @returns {Promise<string>}
     */
    async setTuEditorToHtml(html) {
        if (!html) return html;
        const wrap = document.createElement("div");
        wrap.innerHTML = html;
        const imgEls = Array.from(wrap.querySelectorAll("img"));
        for (let i = 0; i < imgEls.length; i++) {
            const img = imgEls[i];
            const src = img.getAttribute("src") || "";
            if (!src.startsWith("data:image/")) continue;
            const file = this.dataUrlToFile(src, `editor-image-${i + 1}`);
            const res = await this.setEditorImageUpload(file);
            const realUrl = res?.data?.data?.image?.path;
            if (realUrl) {
                img.setAttribute("src", realUrl);
                if (!img.getAttribute("alt")) img.setAttribute("alt", "image");
            }
        }
        return wrap.innerHTML;
    }

    /**
     * DataURL 문자열을 File 객체로 변환하는 함수
     *
     * @param {string} dataUrl Base64 DataURL
     * @param {string} baseName 파일명
     * @returns {File}
     */
    dataUrlToFile(dataUrl, baseName) {
        const [meta, base64] = dataUrl.split(",");
        const mimeMatch = meta.match(/data:(.*?);base64/);
        const mime = mimeMatch ? mimeMatch[1] : "image/png";
        const ext = mime.includes("jpeg") ? "jpg" : mime.split("/")[1] || "png";
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        return new File([blob], `${baseName}.${ext}`, { type: mime });
    }

    /**
     * 게시판 등록 및 수정 저장 함수
     *
     * @param {string} menuId 메뉴 ID
     * @param {Object|undefined} data 수정 데이터
     * @param {string|undefined} type 저장 타입
     * @param {string} rootId 루트 메뉴 ID
     * @returns {Promise<AxiosResponse>}
     */
    async setBoardFormData(menuId, data = undefined, type = undefined, rootId) {
        const formData = new FormData();
        let fileInfos = [];
        let contentData = {};
        const folded = foldTelFields(this.listInnerData, ["tel01", "tel02", "tel03"], "tel");
        const existingInfos = [];
        const newFiles = [];
        if (type === "list") {
            const normalized = await this.setTuEditorToHtml(this.listHtml);
            this.listHtml = normalized;
            folded["listContent"] = normalized;
        }
        if (type === "partners") {
            Object.keys(folded).forEach((key) => {
                if (/^contents/.test(key)) contentData[key] = folded[key];
            });
        } else {
            const { zonecode, isActive, ...rest } = folded || {};
            contentData = rest;
        }
        const contentInner = Object.values(contentData || {}).filter((v) => v !== null && v !== "").join(" ");
        let formDataContent = type === "list" ? setHtmlRemovePrint(this.listHtml) : contentInner
        if(!formDataContent) {
            formDataContent = " ";
        }
        if(data){
            formData.append("contentId", data._id);
        }
        for (const file of this.formFile) {
            if (file && typeof file.md5 === "string") {
                existingInfos.push(file);
            } else {
                newFiles.push(file);
            }
        }
        for (const newFile of newFiles) {
            formData.append("files", newFile);
        }
        if (newFiles.length) {
            const md5List = await Promise.all(newFiles.map(nf => md5OfFile(nf)));
            const newInfos = md5List.map((hash, i) => ({
                md5: hash,
                name: newFiles[i].name,
            }));
            fileInfos = existingInfos.concat(newInfos);
        } else {
            fileInfos = existingInfos;
        }

        if (fileInfos.length) {
            formData.append("fileInfos", JSON.stringify(fileInfos));
        }
        formData.append("menuId", menuId);
        formData.append("rootMenuId", rootId);
        formData.append("type", this.currentBoardContent.type);
        formData.append("subType", this.currentBoardContent.subType);
        formData.append("title", this.listInnerData.title);
        formData.append("data", JSON.stringify(folded));

        formData.append("content", formDataContent);
        return await axios.post("/api/content", formData);
    }


    /**
     * 협력사 상세 데이터 세팅 함수
     *
     * @param {Object} data 협력사 데이터
     */
    setCooperationInnerData(data){
        if(data.address){
            this.setCooperationDataList("address", data.address)
        }
        if(data.detailAddress){
            this.setCooperationDataList("detailAddress", data.detailAddress)
        }
        if(data.locationTitle){
            this.setCooperationDataList("locationTitle", data.locationTitle)
        }
        if(data.tel){
            this.setCooperationDataList("tel01", data.tel.split("-")[0])
            this.setCooperationDataList("tel02", data.tel.split("-")[1])
            this.setCooperationDataList("tel03", data.tel.split("-")[2])
        }
        if(data.files && data.files.length > 0){
            this.formFile = data.files;
            this.imagePreviewMap = updateImagePreview(this.formFile);
        }
        if(data.title){
            this.setCooperationDataList("title", data.title)
        }
        if(data.homepageUrl){
            this.setCooperationDataList("homepageUrl", data.homepageUrl)
        }
        if(data.isActive){
            this.setCooperationDataList("isActive", data.isActive)
        }
        if(data.contents){
            this.setCooperationDataList("contents", data.contents)
        }
        if(data.connectUrl){
            this.setCooperationDataList("connectUrl", data.connectUrl)
        }
    }

    /**
     * 게시글 삭제 함수
     *
     * @param {string} name 체크박스 그룹명
     * @param {Array<Object>} list 게시글 리스트
     * @returns {Promise<AxiosResponse>}
     */
    async setDeleteContent(name, list){
        const selectedItems = this.setSelectCheckList(name, list);
        const ids = selectedItems.map(item => item?._id);
        const payLoad = {
            contentIds: ids,
        }
        return await axios.delete("/api/content", {data: payLoad});
    }

    /**
     * 게시글 다운로드 데이터 조회 함수
     *
     * @param {string} id 메뉴 ID
     * @param {number} count 조회 개수
     * @returns {Promise<Array<Object>|undefined>}
     */
    async setDownLoadContent(id, count) {
        const response = await axios.get(`/api/content?menuId=${id}&limit=${count}`);
        if(response){
            const resultData = [];
            for (const row of response.data.data.contents) {
                resultData.push(row.data);
            }
            return resultData;
        }
    }

    /**
     * 기간별 게시글 조회 함수
     *
     * @param {string} id 메뉴 ID
     * @param {string} startDate 시작일
     * @param {string} endDate 종료일
     * @returns {Promise<Array<Object>|undefined>}
     */
    async getTermData(id, startDate, endDate){
        endDate = moment(endDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
        const response = await axios.get(`/api/content/term?menuId=${id}&startDate=${startDate}&endDate=${endDate}`);
        if(response){
            const resultData = [];
            for (const row of response.data.data.contents) {
                if(row.updatedAt) row.data["updatedAt"] = moment(row.updatedAt).tz("Asia/Seoul").format("YYYY-MM-DD HH:mm:ss");
                resultData.push(row.data);
            }
            return resultData;
        }
    }

    /**
     * 체크된 리스트 데이터 반환 함수
     *
     * @param {string} name 체크박스 그룹명
     * @param {Array<Object>} list 데이터 리스트
     * @returns {Array<Object>|undefined}
     */
    setSelectCheckList(name, list) {
        if(list && list.length > 0){
            if (!this.searchBaseStore.checkedMap[name] || this.searchBaseStore.checkedMap[name].length === 0) return;
            return this.searchBaseStore.checkedMap[name].map(index => list[index]);
        }
    }


    /**
     * 게시판 HTML 데이터 세팅 함수
     *
     * @param {string} html HTML 문자열
     */
    setListSettingEditor(html){
        this.listHtml = html;
    }

    /**
     * 에디터 이미지 업로드 함수
     *
     * @param {File|Blob} blob 업로드 이미지 데이터
     * @returns {Promise<AxiosResponse>}
     */
    async setEditorImageUpload(blob) {
        const md5 = await md5OfFile(blob);
        const info = { md5, name: blob?.name };
        const formData = new FormData();
        formData.append("file", blob);
        formData.append("image", JSON.stringify(info));
        return await axios.post("/api/content/image", formData);
    }

    /**
     * 현재 게시판 메뉴 데이터 세팅 함수
     *
     * @param {Array<Object>} list 메뉴 리스트
     * @param {string} id 메뉴 ID
     * @param {string} flatParams URL Query String
     * @param {string|undefined} contentId 컨텐츠 ID
     * @returns {Promise<void>}
     */
    async setBoardMenuContent(list, id, flatParams, contentId = undefined) {
        this.setInitBoard();
        this.searchBaseStore.clearBase();
        this.currentBoardContent = setFindItemId(list, id);
        if(this.currentBoardContent){
            if(flatParams){
                if (this.currentBoardContent.options.limit) {
                    this.searchMap["limit"] = this.currentBoardContent.options.limit;
                }
                const params = this.searchBaseStore.setCurrentParams(flatParams, this.searchMap);
                await this.setFetchBoardList(params);
            }
            if(contentId){
                await this.setFetchBoardData(contentId);
            }
            if(this.currentBoardContent.type && this.currentBoardContent.type === "FORM"){
                this.formSearchSelectList = this.buildFormSelectBox(this.currentBoardContent.form);
                this.formTableInner = this.buildDynamicFormColumns(this.currentBoardContent.form)
            }
        }
    }

    /**
     * FORM SelectBox 데이터 생성 함수
     *
     * @param {Array<Object>} list FORM 리스트
     * @returns {Array<Object>|undefined}
     */
    buildFormSelectBox(list) {
        if(list){
            return list.filter(item => item && item.type !== "agree" && item.type !== "files" && item.required).map(item => ({value: `row${item._id}`, label: item.name}));
        }
    }

    /**
     * FORM 타입별 데이터 렌더링 함수
     *
     * @param {string} type FORM 타입
     * @param {*} cell 셀 데이터
     * @returns {string}
     */
    renderByType(type, cell) {
        if (cell == null || cell === "") return "-";
        switch (type) {
            case "content":
                return ellipsis(stripHtml(cell), 100);
            default:
                return String(cell);
        }
    }

    /**
     * FORM 테이블 컬럼 생성 함수
     *
     * @param {Array<Object>} formArray FORM 데이터 배열
     * @returns {Array<Object>}
     */
    buildDynamicFormColumns(formArray) {
        if (!Array.isArray(formArray) || formArray.length === 0) return [];
        const idToType = Object.fromEntries((formArray ?? []).map(form => [form._id, form.type]));
        return this.buildFormSelectBox(formArray).map(({ value, label }) => {
            const id = value.startsWith("row") ? value.slice(3) : null;
            const type = id ? idToType[id] : undefined;
            return {
                label,
                minWidth: "120px",
                render: (_, row) => this.renderByType(type, row?.[value]),
            };
        });
    }

    /**
     * FORM 상세 데이터 순서 정렬 함수
     *
     * @param {Object} data 상세 데이터
     * @returns {Array<Object>}
     */
    setFormDetailOrder(data){
        const list = [];
        for (const item of this.currentBoardContent.form) {
            const id = String(item._id);
            const key = `row${id}`;
            if (!(key in data)) continue;
            const value = data[key];
            list.push({ name: item.name, value });
        }
        return list;
    }

    /**
     * 뉴스레터 구독 리스트 조회 함수
     *
     * @param {number} offset 시작 위치
     * @param {number} limit 조회 개수
     * @param {string} word 검색어
     * @returns {Promise<void>}
     */
    async setSubscribeList(offset = 0, limit = 50, word = "") {
        let search = "";
        if(word){
            search = `&searchString=${word}`
        }
        this.setSubscribeInitState(word);
        const response = await axios.get(`/api/subscribe?offset=${offset}&limit=${limit}${search}`);
        if(response){
            runInAction(() => {
                this.newsSubscribeList = response.data.data.subscribers;
                this.newsSubscribeTotalCount = response.data.data.count;
            });
        }
    }

    /**
     * 뉴스레터 다운로드 데이터 조회 함수
     *
     * @param {number} count 조회 개수
     * @returns {Promise<Array<Object>|undefined>}
     */
    async setDownLoadSubscribe(count) {
        let search = "";
        if(this.searchSubscribeState){
            search = `&searchString=${this.searchSubscribeState}`
        }
        const response = await axios.get(`/api/subscribe?limit=${count}${search}`);
        if(response){
            return response.data.data.subscribers;
        }
    }

    /**
     * 뉴스레터 검색 상태 세팅 함수
     *
     * @param {string} value 검색어
     */
    setSubscribeInitState(value){
        this.searchSubscribeState = value;
    }

    /**
     * 뉴스레터 구독 삭제 함수
     *
     * @param {string} name 체크박스 그룹명
     * @param {Array<Object>} list 구독 리스트
     * @returns {Promise<AxiosResponse>}
     */
    async setDeleteSubscribe(name, list) {
        const selectedItems = this.setSelectCheckList(name, list);
        const items = selectedItems.map(item => item);
        const payLoad = {
            emails: items,
        }
        return await axios.delete("/api/subscribe", {data: payLoad});
    }

    /**
     * 협력사 브랜드 정렬 데이터 조회 함수
     *
     * @returns {Promise<void>}
     */
    async getCooperationBrandOrder() {
        const response = await axios.get("/api/cooperation");
        if(response){
            const list = response?.data?.data?.cooperation || [];
            runInAction(() => {
                this.cooperationBradOrderList = list.length > 0 ? list.map(item => ({brand: item.brand, endDate: item.endDate,})) : [{ brand: "", endDate: "" }];
            });
        }
    }

    /**
     * 협력사 브랜드 정렬 데이터 수정 함수
     *
     * @param {number} index 배열 인덱스
     * @param {string} key 수정 키값
     * @param {*} value 저장 값
     */
    updateOrderRow(index, key, value) {
        this.cooperationBradOrderList[index][key] = value;
    }

    /**
     * 협력사 브랜드 정렬 행 추가 함수
     *
     * @returns {number}
     */
    addOrderRow() {
        this.cooperationBradOrderList.push({
            brand: "",
            endDate: "",
        });
        return this.cooperationBradOrderList.length - 1;
    }

    /**
     * 협력사 브랜드 정렬 행 삭제 함수
     *
     * @param {number} index 삭제할 인덱스
     */
    removeOrderRow(index) {
        if (this.cooperationBradOrderList.length === 1) return;
        this.cooperationBradOrderList.splice(index, 1);
    }

    /**
     * 협력사 브랜드 정렬 저장 함수
     *
     * @param {Array<Object>} list 저장 데이터
     * @returns {Promise<AxiosResponse>}
     */
    async setSaveCooperationBrandOrder(list){
        return await axios.post("/api/cooperation", {data: list});
    }

    /**
     * 뉴스레터 상태 초기화 함수
     */
    setSubscribeInit() {
        this.newsSubscribeList = [];
        this.newsSubscribeTotalCount = 0;
        this.searchSubscribeState = "";
    }

    /**
     * 게시판 관리 데이터 초기화 함수
     */
    setListManageInit() {
        this.listHtml = null;
        this.formFile = [];
        this.currentBoardContent = null;
        this.boardCreatDate = null;
        this.boardWriter = null;
    }


    /**
     * 게시판 관련 상태 초기화 함수
     */
    setInitBoard(){
        this.dataList = [];
        this.totalCount = 0;
        this.listInnerData = {}
        this.searchMap = {
            searchType: "",
            searchString: "",
            offset: 0
        }
        this.formSearchSelectList = [];
        this.formTableInner = [];
        this.formFile = [];
        this.currentBoardContent = null;
        this.listHtml = null;
    }

    /**
     * 게시판 FORM 데이터 초기화 함수
     */
    setBoardFormDataReset() {
        this.imagePreviewMap = {};
        this.listInnerData = {}
        this.formFile = [];
    }

    clearBrandOrder() {
        this.cooperationBradOrderList = [];
    }

    /**
     * 공통 검색 파라미터 초기화 함수
     *
     * @param {Object} param 초기화할 파라미터
     */
    clearParams(param) {
        this.searchBaseStore.searchParams = param;
    }

}

/**
 * BoardDetailStore Hook
 *
 * @returns {BoardDetailStore}
 */
export const useBoardDetailStore = () => {
    return useStore().boardDetailStore;
}

/**
 * BoardDetailStore 직접 접근 함수
 *
 * @returns {BoardDetailStore}
 */
export const getBoardDetailStore = () => {
    return getStore().boardDetailStore;
}
